/**
 * Professional Homepage — Cluster template aesthetic + production content + Phase 1 features.
 * Fuses: Cluster-style layout/typography, production use cases/features/footer, Phase 1 Report flow & CTAs.
 */
import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Ambulance,
  Users,
  Baby,
  Pill,
  QrCode,
  ShoppingBag,
  Menu,
  X,
  AlertCircle,
  Check,
} from "lucide-react";
import QRScanner from "../components/QRScanner";
import DemoModal from "../components/DemoModal";
import BrandLogo from "../components/shared/BrandLogo";
import LanguageSwitcher from "../components/shared/LanguageSwitcher";
import EmergencyLocketFeatures from "../components/ui/feature-grid-enterprise-grade";
import HeroSection from "../components/homepage/HeroSection";
import HowItWorksSection from "../components/homepage/HowItWorksSection";
import FooterSection from "../components/homepage/FooterSection";
import { PLANS, formatPrice, ACTIVE_SUBSCRIPTION_PLANS } from "../config/pricing";
import FAQSection from "../components/landing/FAQSection";
import TestimonialsSection from "../components/landing/TestimonialsSection";
import { useLanguage } from "../contexts/LanguageContext";
import { useAuth } from "../contexts/AuthContext";

const SECTION_IDS = {
  home: "home",
  useCases: "use-cases",
  features: "features",
  pricing: "pricing",
  howToSetUp: "how-to-set-up",
  faq: "faq",
};

const PRICING_VIEW = { ONE_TIME: "one_time", PREMIUM: "premium" };

const useCases = [
  {
    IconComponent: Ambulance,
    titleKey: "useCases.uc1Title",
    descKey: "useCases.uc1Desc",
    detailKey: "useCases.uc1Detail",
    statKey: "useCases.uc1Stat",
    colors: "from-red-50 to-red-100 border-red-200",
    iconBg: "bg-red-100",
    textColor: "text-red-700",
  },
  {
    IconComponent: Users,
    titleKey: "useCases.uc2Title",
    descKey: "useCases.uc2Desc",
    detailKey: "useCases.uc2Detail",
    statKey: "useCases.uc2Stat",
    colors: "from-amber-50 to-amber-100 border-amber-200",
    iconBg: "bg-amber-100",
    textColor: "text-amber-700",
  },
  {
    IconComponent: Baby,
    titleKey: "useCases.uc3Title",
    descKey: "useCases.uc3Desc",
    detailKey: "useCases.uc3Detail",
    statKey: "useCases.uc3Stat",
    colors: "from-pink-50 to-pink-100 border-pink-200",
    iconBg: "bg-pink-100",
    textColor: "text-pink-700",
  },
  {
    IconComponent: Pill,
    titleKey: "useCases.uc4Title",
    descKey: "useCases.uc4Desc",
    detailKey: "useCases.uc4Detail",
    statKey: "useCases.uc4Stat",
    colors: "from-purple-50 to-purple-100 border-purple-200",
    iconBg: "bg-purple-100",
    textColor: "text-purple-700",
  },
];

const ProfessionalHomePage = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { isAuthenticated, user, logout } = useAuth();
  const scrollContainerRef = useRef(null);
  const [showQRScanner, setShowQRScanner] = useState(false);
  const [showDemoModal, setShowDemoModal] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [pricingView, setPricingView] = useState(PRICING_VIEW.ONE_TIME);
  const [activeUseCaseModal, setActiveUseCaseModal] = useState(null);

  const handleQRScan = (locketId) => {
    if (locketId) {
      const id = String(locketId).trim().toUpperCase();
      navigate(`/${id}`);
    }
  };

  const scrollToSection = (sectionId) => {
    setMobileMenuOpen(false);
    const container = scrollContainerRef.current;
    const doScroll = () => {
      const element = document.getElementById(sectionId);
      if (element && container) {
        const headerOffset = 56;
        const elementRect = element.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();
        const targetScroll = Math.max(0, container.scrollTop + (elementRect.top - containerRect.top) - headerOffset);
        const start = container.scrollTop;
        const distance = targetScroll - start;
        const duration = 700;
        const startTime = performance.now();
        const easeOutCubic = (t) => 1 - (1 - t) ** 3;
        const animate = (now) => {
          const elapsed = now - startTime;
          const progress = Math.min(elapsed / duration, 1);
          container.scrollTop = start + distance * easeOutCubic(progress);
          if (progress < 1) requestAnimationFrame(animate);
        };
        requestAnimationFrame(animate);
      }
    };
    setTimeout(doScroll, 50);
  };

  // Auto-rotation removed for better mobile UX

  const containerClass = "max-w-[1140px] mx-auto px-3 sm:px-6";
  const sectionPadding = "py-5 sm:py-6 lg:py-10";

  return (
    <div ref={scrollContainerRef} className="h-screen overflow-y-auto overflow-x-hidden bg-white font-sans antialiased pb-12 lg:pb-0">
      {/* ----- Header ----- */}
      <header className="sticky top-0 z-40 bg-white border-b border-slate-200/80">
        <div className={containerClass}>
          <div className="flex items-center justify-between h-10 lg:h-14">
            <BrandLogo logoClassName="h-7 lg:h-9" />

            <nav className="hidden lg:flex items-center gap-6">
              <button
                onClick={() => scrollToSection(SECTION_IDS.home)}
                className="text-slate-600 hover:text-slate-900 font-medium text-sm"
              >
                {t("nav.home")}
              </button>
              <button
                onClick={() => scrollToSection(SECTION_IDS.useCases)}
                className="text-slate-600 hover:text-slate-900 font-medium text-sm"
              >
                {t("nav.useCases")}
              </button>
              <button
                onClick={() => scrollToSection(SECTION_IDS.features)}
                className="text-slate-600 hover:text-slate-900 font-medium text-sm"
              >
                {t("nav.features")}
              </button>
              <button
                onClick={() => scrollToSection(SECTION_IDS.pricing)}
                className="text-slate-600 hover:text-slate-900 font-medium text-sm"
              >
                {t("nav.pricing")}
              </button>
              <button
                onClick={() => scrollToSection(SECTION_IDS.howToSetUp)}
                className="text-slate-600 hover:text-slate-900 font-medium text-sm"
              >
                {t("nav.howToSetUp")}
              </button>
              <button
                onClick={() => scrollToSection(SECTION_IDS.faq)}
                className="text-slate-600 hover:text-slate-900 font-medium text-sm"
              >
                {t("nav.faq")}
              </button>
            </nav>

            <div className="hidden lg:flex items-center gap-3">
              <button
                onClick={() => setShowDemoModal(true)}
                className="text-slate-600 hover:text-slate-900 font-medium text-sm px-3 py-2"
              >
                {t("nav.seeDemo")}
              </button>
              <button
                onClick={() => setShowQRScanner(true)}
                className="inline-flex items-center gap-1.5 text-slate-700 hover:text-red-600 font-semibold text-sm px-3 py-2 transition-colors"
              >
                <AlertCircle size={16} /> {t("nav.reportEmergency")}
              </button>
              <button
                onClick={() => navigate('/order')}
                className="inline-flex items-center gap-2 bg-slate-900 text-white font-semibold text-sm px-4 py-2.5 rounded-lg hover:bg-slate-800 transition-colors"
              >
                <ShoppingBag size={18} />
                {t("nav.order")}
              </button>
              <button
                onClick={() => setShowQRScanner(true)}
                className="inline-flex items-center gap-2 bg-red-600 text-white font-semibold text-sm px-4 py-2.5 rounded-lg hover:bg-red-700 transition-colors"
              >
                <QrCode size={18} />
                {t("nav.scanLogin")}
              </button>
              {isAuthenticated ? (
                <div className="relative group">
                  <button className="inline-flex items-center gap-1.5 text-slate-700 font-semibold text-sm px-3 py-2 rounded-lg hover:bg-slate-100 transition-colors">
                    {user?.profile?.name?.split(" ")[0] || t("nav.profile")}
                  </button>
                  <div className="absolute right-0 top-full mt-1 bg-white border border-slate-200 rounded-lg shadow-md py-1 min-w-[140px] hidden group-hover:block">
                    <button onClick={() => navigate("/profile")} className="block w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-50">{t("nav.profile")}</button>
                    <button onClick={logout} className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-slate-50">{t("nav.logout")}</button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => setShowQRScanner(true)}
                  className="text-slate-700 font-semibold text-sm px-3 py-2 hover:text-red-600 transition-colors"
                >
                  {t("nav.login")}
                </button>
              )}
            </div>

            <button
              type="button"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden p-2 text-slate-700"
              aria-label="Menu"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>

          {mobileMenuOpen && (
            <div className="lg:hidden border-t border-slate-200 py-4 space-y-1">
              {[
                { labelKey: "nav.home", id: SECTION_IDS.home },
                { labelKey: "nav.useCases", id: SECTION_IDS.useCases },
                { labelKey: "nav.features", id: SECTION_IDS.features },
                { labelKey: "nav.pricing", id: SECTION_IDS.pricing },
                { labelKey: "nav.howToSetUp", id: SECTION_IDS.howToSetUp },
                { labelKey: "nav.faq", id: SECTION_IDS.faq },
              ].map(({ labelKey, id }) => (
                <button
                  key={id}
                  onClick={() => scrollToSection(id)}
                  className="block w-full text-left py-2.5 px-3 text-slate-700 font-medium rounded-lg hover:bg-slate-50"
                >
                  {t(labelKey)}
                </button>
              ))}
              <button
                onClick={() => {
                  setShowQRScanner(true);
                  setMobileMenuOpen(false);
                }}
                className="flex items-center gap-2 py-2.5 px-3 text-slate-700 font-medium rounded-lg hover:bg-slate-50 w-full text-left"
              >
                <AlertCircle size={18} /> {t("nav.reportEmergency")}
              </button>
              <button
                onClick={() => {
                  setShowDemoModal(true);
                  setMobileMenuOpen(false);
                }}
                className="block w-full text-left py-2.5 px-3 text-slate-700 font-medium rounded-lg hover:bg-slate-50"
              >
                {t("nav.seeDemo")}
              </button>
              <button
                onClick={() => {
                  navigate('/order');
                  setMobileMenuOpen(false);
                }}
                className="w-full flex items-center justify-center gap-2 bg-slate-900 text-white font-semibold py-3 rounded-lg mt-2"
              >
                <ShoppingBag size={18} /> {t("nav.order")}
              </button>
              <button
                onClick={() => {
                  setShowQRScanner(true);
                  setMobileMenuOpen(false);
                }}
                className="w-full flex items-center justify-center gap-2 bg-red-600 text-white font-semibold py-3 rounded-lg mt-2"
              >
                <QrCode size={18} /> Scan
              </button>
            </div>
          )}
        </div>
      </header>

      {/* ----- Hero ----- */}
      <section id={SECTION_IDS.home} className="pt-0 pb-8 sm:pb-10 lg:pb-12">
        <HeroSection onScanQR={() => setShowQRScanner(true)} onGetQRCode={() => navigate('/order')} />
      </section>

      {/* ----- Use Cases — static 2×2 grid on mobile ----- */}
      <section id={SECTION_IDS.useCases} className={`${sectionPadding} bg-white`}>
        <div className={containerClass}>
          <div className="text-center mb-4 sm:mb-6">
            <h2 className="text-xl sm:text-3xl lg:text-4xl font-extrabold text-slate-900 mb-1">{t("useCases.title")}</h2>
            <p className="text-slate-600 text-sm sm:text-base max-w-2xl mx-auto">{t("useCases.subtitle")}</p>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-4 max-w-4xl mx-auto">
            {useCases.map((uc, idx) => {
              const Icon = uc.IconComponent;
              return (
                <div
                  key={idx}
                  onClick={() => setActiveUseCaseModal(idx)}
                  className={`${uc.iconBg} border ${uc.colors.split(' ').find(c => c.startsWith('border-'))} rounded-lg p-3 sm:p-4 text-center flex flex-col cursor-pointer hover:shadow-md transition-shadow duration-150`}
                >
                  <div className={`w-10 h-10 bg-white/60 rounded-lg flex items-center justify-center mx-auto mb-2`}>
                    <Icon size={20} className={uc.textColor} />
                  </div>
                  <h3 className={`text-sm sm:text-base font-bold ${uc.textColor} mb-0.5`}>{t(uc.titleKey)}</h3>
                  <span className={`text-xs ${uc.textColor} opacity-80 mb-2`}>{t(uc.statKey)}</span>
                  <span className={`mt-auto text-xs font-semibold ${uc.textColor}`}>
                    {t("useCases.knowMore")} &rarr;
                  </span>
                </div>
              );
            })}
          </div>

          {/* Use Case Detail Modal */}
          {activeUseCaseModal !== null && (() => {
            const uc = useCases[activeUseCaseModal];
            const Icon = uc.IconComponent;
            return (
              <div
                className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
                onClick={() => setActiveUseCaseModal(null)}
              >
                <div
                  className="bg-white rounded-lg shadow-md max-w-md w-full p-5 border border-slate-200 max-h-[80vh] overflow-y-auto"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className={`w-10 h-10 ${uc.iconBg} rounded-lg flex items-center justify-center`}>
                        <Icon size={20} className={uc.textColor} />
                      </div>
                      <h3 className="text-lg font-bold text-slate-900">{t(uc.titleKey)}</h3>
                    </div>
                    <button onClick={() => setActiveUseCaseModal(null)} className="text-slate-400 hover:text-slate-600 p-1">
                      <X size={20} />
                    </button>
                  </div>
                  <p className="text-sm text-slate-600 leading-relaxed mb-3">{t(uc.descKey)}</p>
                  <p className="text-sm text-slate-700 leading-relaxed mb-4">{t(uc.detailKey)}</p>
                  <div className={`rounded-lg p-2.5 ${uc.iconBg} mb-3`}>
                    <p className={`text-xs font-bold ${uc.textColor}`}>{t(uc.statKey)}</p>
                  </div>
                  <button
                    onClick={() => {
                      setActiveUseCaseModal(null);
                      navigate('/order');
                    }}
                    className="w-full bg-red-600 hover:bg-red-700 text-white font-bold py-3 rounded-lg transition-colors duration-150 text-sm"
                  >
                    {t("useCases.protectCta")}
                  </button>
                </div>
              </div>
            );
          })()}
        </div>
      </section>

      {/* ----- Features ----- */}
      <section id={SECTION_IDS.features} className={`${sectionPadding} bg-slate-50/80`}>
        <div className={containerClass}>
          <EmergencyLocketFeatures sectionTitle={t("features.sectionTitle")} sectionSubtitle={t("features.sectionSubtitle")} />
        </div>
      </section>

      {/* ----- Pricing (One-time + Premium toggle) ----- */}
      <section id={SECTION_IDS.pricing} className={`${sectionPadding} bg-white`}>
        <div className={containerClass}>
          <div className="text-center mb-6 sm:mb-8">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-slate-900 mb-2">{t("pricing.title")}</h2>
            {/* Toggle: One-time purchase | Premium membership */}
            <div className="inline-flex p-1 rounded-lg bg-slate-200/80 border border-slate-200 mt-3">
              <button
                type="button"
                onClick={() => setPricingView(PRICING_VIEW.ONE_TIME)}
                className={`px-4 py-2.5 rounded-lg text-sm font-semibold transition-colors duration-150 ${pricingView === PRICING_VIEW.ONE_TIME ? "bg-white text-slate-900 shadow-sm" : "text-slate-600 hover:text-slate-800"}`}
              >
                {t("pricing.oneTimeTab")}
              </button>
              <button
                type="button"
                onClick={() => setPricingView(PRICING_VIEW.PREMIUM)}
                className={`px-4 py-2.5 rounded-lg text-sm font-semibold transition-colors duration-150 ${pricingView === PRICING_VIEW.PREMIUM ? "bg-white text-slate-900 shadow-sm" : "text-slate-600 hover:text-slate-800"}`}
              >
                {t("pricing.premiumTab")}
              </button>
            </div>
            <p className="text-slate-600 max-w-2xl mx-auto text-sm sm:text-base mt-4">
              {pricingView === PRICING_VIEW.ONE_TIME ? t("pricing.oneTimeDesc") : t("pricing.premiumDesc")}
            </p>
          </div>

          {pricingView === PRICING_VIEW.ONE_TIME ? (
            <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
              {[PLANS.family, PLANS.individual].map((plan) => (
                <div
                  key={plan.id}
                  className={`bg-white rounded-lg border-2 p-6 sm:p-8 transition-colors duration-150 ${plan.badge ? "border-red-500 shadow-md shadow-red-500/10" : "border-slate-200 hover:border-slate-300"}`}
                >
                  {plan.badge && (
                    <span className="inline-block bg-red-100 text-red-700 text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full mb-4">
                      {t("pricing.bestValue")}
                    </span>
                  )}
                  <h3 className="text-xl font-bold text-slate-900 mb-1">{t(`pricing.plans.${plan.id}.name`)}</h3>
                  <p className="text-slate-600 text-sm mb-4">{t(`pricing.plans.${plan.id}.desc`)}</p>
                  <div className="flex items-baseline gap-1 mb-6">
                    <span className="text-3xl font-extrabold text-slate-900">{formatPrice(plan.price)}</span>
                    <span className="text-slate-500 text-sm">{t("pricing.oneTimeLabel")}</span>
                  </div>
                  <ul className="space-y-2 mb-6">
                    {[1, 2, 3, 4].map((i) => (
                      <li key={i} className="flex items-center gap-2 text-slate-700 text-sm">
                        <Check size={16} className="text-emerald-600 shrink-0" /> {t(`pricing.plans.${plan.id}.f${i}`)}
                      </li>
                    ))}
                  </ul>
                  <button
                    type="button"
                    onClick={() => navigate('/order')}
                    className={`w-full flex items-center justify-center gap-2 font-bold py-3.5 rounded-lg transition-colors ${plan.badge ? "bg-red-600 text-white hover:bg-red-700" : "bg-slate-900 text-white hover:bg-slate-800"}`}
                  >
                    <ShoppingBag size={18} /> {t("pricing.orderCta")} — {t(`pricing.plans.${plan.id}.name`)}
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
              {ACTIVE_SUBSCRIPTION_PLANS.map((plan) => (
                <div
                  key={plan.id}
                  className={`bg-white rounded-lg border-2 p-6 sm:p-8 transition-colors duration-150 ${plan.badge ? "border-red-500 shadow-md shadow-red-500/10" : "border-slate-200 hover:border-slate-300"}`}
                >
                  {plan.badge && (
                    <span className="inline-block bg-red-100 text-red-700 text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full mb-4">
                      {t("pricing.bestValue")}
                    </span>
                  )}
                  <h3 className="text-xl font-bold text-slate-900 mb-1">{t(`pricing.plans.${plan.id}.name`)}</h3>
                  <p className="text-slate-600 text-sm mb-4">{t(`pricing.plans.${plan.id}.desc`)}</p>
                  <div className="flex items-baseline gap-1 mb-6">
                    <span className="text-3xl font-extrabold text-slate-900">{formatPrice(plan.price)}</span>
                    <span className="text-slate-500 text-sm">{t("pricing.perYear")}</span>
                  </div>
                  <ul className="space-y-2 mb-6">
                    {[1, 2, 3, 4].map((i) => (
                      <li key={i} className="flex items-center gap-2 text-slate-700 text-sm">
                        <Check size={16} className="text-emerald-600 shrink-0" /> {t(`pricing.plans.${plan.id}.f${i}`)}
                      </li>
                    ))}
                  </ul>
                  <button
                    type="button"
                    onClick={() => navigate('/order', { state: { plan } })}
                    className={`w-full flex items-center justify-center gap-2 font-bold py-3.5 rounded-lg transition-colors ${plan.badge ? "bg-red-600 text-white hover:bg-red-700" : "bg-slate-900 text-white hover:bg-slate-800"}`}
                  >
                    <ShoppingBag size={18} /> {t("pricing.orderCta")} — {t(`pricing.plans.${plan.id}.name`)}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ----- How to Set Up ----- */}
      <section id={SECTION_IDS.howToSetUp} className={`${sectionPadding} bg-slate-50/80`}>

        <div className={containerClass}>
          <HowItWorksSection onGetQRCode={() => navigate('/order')} onViewDemo={() => setShowDemoModal(true)} />
        </div>
      </section>

      {/* ----- Testimonials ----- */}
      <section className={`${sectionPadding} bg-white`}>
        <div className={containerClass}>
          <TestimonialsSection />
        </div>
      </section>

      {/* ----- FAQ ----- */}
      <section id={SECTION_IDS.faq} className={`${sectionPadding} bg-slate-50/80`}>
        <div className={containerClass}>
          <FAQSection />
        </div>
      </section>

      {/* ----- Footer ----- */}
      <FooterSection />

      {/* Language switcher — above sticky bar */}
      <div className="fixed bottom-16 right-3 z-50">
        <LanguageSwitcher />
      </div>

      {/* Sticky bottom CTA bar */}
      <div className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-slate-200 shadow-md px-3 py-2 flex gap-2 lg:hidden">
        <button
          type="button"
          onClick={() => navigate('/order')}
          className="flex-1 flex items-center justify-center gap-1.5 bg-slate-900 text-white font-bold text-sm py-2.5 rounded-lg"
        >
          <ShoppingBag size={16} /> {t("nav.order")}
        </button>
        <button
          type="button"
          onClick={() => setShowQRScanner(true)}
          className="flex-1 flex items-center justify-center gap-1.5 bg-red-600 text-white font-bold text-sm py-2.5 rounded-lg"
        >
          <QrCode size={16} /> Scan
        </button>
      </div>

      <QRScanner isOpen={showQRScanner} onScan={handleQRScan} onClose={() => setShowQRScanner(false)} />
      <DemoModal isOpen={showDemoModal} onClose={() => setShowDemoModal(false)} />
    </div>
  );
};

export default ProfessionalHomePage;
